import React from 'react';
import {
  TableHeaderProps,
  TableHeaderVariant,
  TableDensity,
  SortDirection,
  TableColumn,
} from '../../../types/DesignSystem/Table';
import { Sort, ArrowUp, ArrowDown } from '../Iconography/Icons';
import { FOCUS_RING_INSET } from '../focusRing';
import { stickyHeaderCellClasses, stickyHeaderGround, stickyHeaderShadow } from './stickyColumn';

/**
 * Header variant background styles
 */
const headerVariantStyles: Record<TableHeaderVariant, string> = {
  dark: 'tw-bg-tone-neutral-tint',
  light: 'tw-bg-surface-sunken',
};

/** § 245 — one vertical padding per density, shared with `TableCell` so header and body rows agree. */
export const DENSITY_PY: Record<TableDensity, string> = {
  compact: 'tw-py-1.5',
  default: 'tw-py-2',
  comfortable: 'tw-py-3',
};

/**
 * Get sort icon based on current sort state.
 *
 * § 260 — the IDLE glyph is an offer and the ACTIVE one is information, so only the
 * active arrow is furniture. A six-column table used to paint six double-chevrons
 * permanently, none of which said anything until clicked. The idle glyph is still in
 * the DOM (it reserves its own width, so nothing reflows when it appears) and simply
 * has no opacity until the column is hovered or its button takes a visible focus.
 * Screen-reader and keyboard behaviour are untouched: `aria-sort` on the `<th>` is
 * what carries the real state, and the glyph has always been `aria-hidden`.
 */
/**
 * The idle sort glyph RESTS VISIBLE at 90%, and brightens to full on hover or a
 * visible focus. § 307 (2026-09-18) — this replaces § 260's hover-only `opacity-0`.
 *
 * § 260's argument was that the idle glyph is an OFFER and only the active arrow is
 * information, so six idle chevrons are six pieces of furniture that say nothing. That
 * holds for a table you read and fails for a list you WORK: on `/gms/guide` there was
 * no way to tell a sortable column from a plain one without sweeping the pointer along
 * the header. Worse, the mechanism was `group-hover` — **there is no hover on a touch
 * screen**, so on a tablet the affordance never appeared at all. That is a functional
 * gap, not a matter of taste, and it is why this is the default rather than a prop.
 *
 * What survives of § 260 is its real point, carried by two things that cost nothing:
 * the states differ by GLYPH (idle is a double chevron, active a single arrow that
 * takes no opacity class at all), and the idle one is still a rung quieter until you
 * hover it.
 *
 * **90% is measured, not chosen by eye.** The glyph is `aria-hidden` so WCAG 1.4.11
 * does not bind it — but "a control nobody can see" is the bug being fixed, so it
 * clears the 3:1 non-text floor anyway. `ink-tertiary` composited over both header
 * grounds, in both themes:
 *
 * | header ground | light | dark |
 * |---|---|---|
 * | `surface-sunken` (plain) | 4.20 | 5.17 |
 * | `tone-neutral-tint` (striped) | 3.57 | **3.37** |
 *
 * The striped dark band is the worst case and the one Guide uses. 80% puts it at
 * **2.98** and under the floor, which is why the first cut of § 307 at 60% (2.31) was
 * simply wrong — it shipped a chevron you can find only once you know it is there.
 */
const IDLE_SORT = 'tw-opacity-90 group-hover:tw-opacity-100 group-focus-visible:tw-opacity-100 motion-safe:tw-transition-opacity';

const getSortIcon = (
  columnId: string,
  sortState?: { columnId: string; direction: SortDirection }
) => {
  if (!sortState || sortState.columnId !== columnId) {
    return <Sort className={IDLE_SORT} />;
  }
  if (sortState.direction === 'asc') {
    return <ArrowUp />;
  }
  if (sortState.direction === 'desc') {
    return <ArrowDown />;
  }
  return <Sort className={IDLE_SORT} />;
};

/**
 * Get next sort direction in cycle: null -> asc -> desc -> null
 */
const getNextSortDirection = (
  columnId: string,
  sortState?: { columnId: string; direction: SortDirection }
): SortDirection => {
  if (!sortState || sortState.columnId !== columnId) {
    return 'asc';
  }
  if (sortState.direction === 'asc') {
    return 'desc';
  }
  return null;
};

/**
 * Map the sort state onto the `aria-sort` value ARIA expects on the header cell.
 * Only sortable columns get the attribute at all — putting `aria-sort="none"` on a
 * column that cannot be sorted tells a screen-reader user it is sortable.
 */
const getAriaSort = <T,>(
  column: TableColumn<T>,
  sortState?: { columnId: string; direction: SortDirection },
): 'ascending' | 'descending' | 'none' | undefined => {
  if (!column.sortable) return undefined;
  if (!sortState || sortState.columnId !== column.id) return 'none';
  if (sortState.direction === 'asc') return 'ascending';
  if (sortState.direction === 'desc') return 'descending';
  return 'none';
};

/**
 * TableHeader.
 *
 * A sortable column used to be a `<th>` carrying an `onClick`: not focusable, not
 * keyboard-operable, no `aria-sort`, no `scope`, and no stable selector beyond its
 * visible text — the gap this bundle's § 2 has flagged since 2026-08-04. A sortable
 * column is now a real `<button>` filling the cell, and the `<th>` carries the
 * `aria-sort` that ARIA requires on the header cell rather than on the control.
 */
const TableHeader = <T,>({
  columns,
  /* `light` since § 245: a header labels the data and sits behind it. */
  variant = 'light',
  density = 'default',
  stickyHeader = false,
  sortState,
  onSort,
  expandable = false,
  selectionCell,
  scrolled = { under: false, left: false, right: false },
  'data-testid': dataTestId,
}: TableHeaderProps<T>) => {
  const headerBg = headerVariantStyles[variant];
  /*
    A sort hover has to darken whichever header ground it sits on, and there are two:
    `light` is `grey-100` and `dark` is `grey-200`. No step of the ramp works for both
    — `grey-100` is invisible on the light variant, and the next step up from
    `grey-200` is `grey-450`, a jump dark enough to read as a selection rather than a
    hover (every other hover in this design system goes to `grey-100`).

    A 5% `grey-900` tint darkens either ground by the same small amount (8.3% and 6.4%
    relative luminance) and keeps the header text at 11.81:1 / 9.32:1.

    What this replaces was `hover:tw-bg-grey-300`, and there is no `grey-300` in the
    palette — the ramp runs 100, 200, 450, 500, 550, 600, 700, 900. So the class
    emitted nothing and a sortable header had NO hover feedback at all. `grey-450` was
    the first fix and it was wrong in the other direction: nearest numeric neighbour,
    not nearest design intent.
  */
  // Padding lives on the button for sortable columns, so the entire tinted cell is
  // the hit target rather than a smaller region inside it — but the `<th>` still needs
  // `tw-p-0`, not nothing. The HTML UA stylesheet gives `th`/`td` `padding: 1px`, so
  // passing '' left a sortable header's label 13px from the cell edge where a plain
  // cell's is 12px. Measured, not guessed: `getComputedStyle(th).paddingLeft` came back
  // `"1px"` and every sortable column drifted exactly 1px right of its own data.
  // `grey-700`, not `grey-900`: a column header labels the data below it, exactly as a
  // field label labels its input, so it should sit behind that data (`TableCell` is
  // `grey-900`). It is NOT `grey-600` like `FieldTitle` — the header sits on a tinted
  // ground, where grey-600 is only 3.26:1 on `grey-200` (the `dark` default) and
  // 4.14:1 on `grey-100`, both under WCAG AA. grey-700 is 5.18:1 / 6.57:1 — it passes
  // on both and still halves the contrast against the cells. Measured, because reusing
  // FieldTitle's white-ground answer here would have shipped illegible headers.
  //
  // No `tw-font-semibold`: `title-header` IS 14px/600, so it was a no-op (verified in
  // a browser — the class changes nothing). The weight now comes from the token alone.
  const headerCellBase =
    'tw-text-title-header tw-text-ink-secondary tw-whitespace-nowrap';
  const py = DENSITY_PY[density];

  const handleSort = (column: TableColumn<T>) => {
    if (column.sortable && onSort) {
      onSort(column.id, getNextSortDirection(column.id, sortState));
    }
  };

  return (
    <thead className={headerBg}>
      <tr>
        {selectionCell !== undefined && (
          /* § 299 — the select-all cell leads the grid. Unlabelled as a column (the box
             names itself); the same pinned ground and shadow rules as the expand cell. */
          <th
            scope="col"
            className={`${headerCellBase} tw-px-3 ${py} tw-w-12 ${stickyHeaderCellClasses(undefined, stickyHeader)} ${stickyHeaderGround(
              stickyHeader,
              variant,
            )} ${stickyHeaderShadow(undefined, stickyHeader, scrolled)}`}
            data-testid={dataTestId ? `${dataTestId}-header-select` : undefined}
          >
            {selectionCell}
          </th>
        )}
        {columns.map((column) => {
          const alignClass =
            column.align === 'center'
              ? 'tw-text-center'
              : column.align === 'right'
                ? 'tw-text-right'
                : 'tw-text-left';

          const justifyClass =
            column.align === 'center'
              ? 'tw-justify-center'
              : column.align === 'right'
                ? 'tw-justify-end'
                : 'tw-justify-start';

          const label = (
            <>
              <span>{column.header}</span>
              {column.sortable && (
                <span
                  aria-hidden="true"
                  className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-ink-tertiary"
                >
                  {getSortIcon(column.id, sortState)}
                </span>
              )}
            </>
          );

          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={getAriaSort(column, sortState)}
              /* A pinned header cell paints the header ground on ITSELF. The ground on
                 `<thead>` sits in the table's own coordinate space, so where a pinned
                 `<th>` is composited over a neighbour — including that neighbour's
                 sort hover tint — an unpainted cell shows it through. Read from
                 `variant`, so a `light` header keeps a light pinned column. */
              className={`${headerCellBase} ${alignClass} ${
                column.sortable ? 'tw-p-0' : `tw-px-3 ${py}`
              } ${stickyHeaderCellClasses(column.sticky, stickyHeader)} ${stickyHeaderGround(
                Boolean(column.sticky) || stickyHeader,
                variant,
              )} ${stickyHeaderShadow(column.sticky, stickyHeader, scrolled)} ${column.headerClassName || ''}`}
              style={{
                width: column.width,
                minWidth: column.minWidth,
              }}
              data-testid={dataTestId ? `${dataTestId}-header-${column.id}` : undefined}
            >
              {/*
                `headerInfo` is a SIBLING of the sort button, never inside it — see
                `TableColumn.headerInfo`. On a sortable column that keeps the sort
                target and the explanation as two separate controls; on an unsortable
                one it simply sits beside the label.
              */}
              {column.sortable ? (
                <div className={`tw-flex tw-items-center ${justifyClass}`}>
                  <button
                    type="button"
                    onClick={() => handleSort(column)}
                    data-testid={dataTestId ? `${dataTestId}-sort-${column.id}` : undefined}
                    // The inset ring, not the offset one: the table wrapper sets
                    // overflow-x-auto and the bordered variant overflow-hidden, so a
                    // ring drawn outside the cell is clipped at the table edge (§ 8).
                    className={`tw-w-full tw-flex tw-items-center tw-gap-1 tw-px-3 ${py} ${justifyClass} tw-group tw-select-none tw-cursor-pointer motion-safe:tw-transition-colors motion-safe:tw-duration-instant motion-safe:tw-ease-swift hover:tw-bg-tone-neutral-tint active:tw-bg-tone-neutral-tint ${FOCUS_RING_INSET}`}
                  >
                    {label}
                  </button>
                  {column.headerInfo ? <span className="tw-pe-3 tw-flex tw-items-center">{column.headerInfo}</span> : null}
                </div>
              ) : (
                <div className={`tw-flex tw-items-center tw-gap-1 ${justifyClass}`}>
                  {label}
                  {column.headerInfo}
                </div>
              )}
            </th>
          );
        })}

        {expandable && (
          /* Unlabelled by design — the column holds chevrons, and each one names
             itself. A visible "Expand" heading would be repeated on every row by
             screen readers that announce the column header with the cell. */
          <th
            scope="col"
            className={`${headerCellBase} tw-px-3 ${py} tw-w-12 ${stickyHeaderGround(
              stickyHeader,
              variant,
            )} ${stickyHeaderShadow(undefined, stickyHeader, scrolled)}`}
            data-testid={dataTestId ? `${dataTestId}-header-expand` : undefined}
          />
        )}
      </tr>
    </thead>
  );
};

export default TableHeader;
